<template>
  <RfBaseModal
    ref="trafficEditDialog"
    width="600"
    title="Traffic Distribution"
    @close="showRunButton = false"
  >
    <template #body>
      <template v-if="showRunButton">
        <RfAlert type="error" class="mb-4">
          <template v-if="isLiveEditable"
            >This experiment will be editable, which could compromise the results.</template
          >
          <template v-else>This experiment will not be editable after it starts.</template>
        </RfAlert>
        <RfAlert v-if="showPromptMessage" type="warning" class="mb-4">
          Your prompt will be published.</RfAlert
        >
      </template>
      <RfAlert :type="totalSumTraffic !== 100 ? 'error' : 'success'" class="mb-4">
        <span>
          Your experiment's traffic distribution must add up to 100%.
          <span class="font-bold">Currently: {{ totalSumTraffic }}%</span>
        </span>
      </RfAlert>
      <RfTable :tableData="{ totalCount: variations?.length, loadMore: 5 }">
        <template #thead>
          <tr>
            <th class="w-3/4">
              <RfSortButton class="h-full w-full" disabled>Variation Name</RfSortButton>
            </th>
            <th class="w-1/4">
              <RfSortButton class="h-full w-full" disabled>Traffic %</RfSortButton>
            </th>
          </tr>
        </template>
        <template #tbody>
          <tr
            v-for="variant in variations"
            :key="variant.id"
            class="text-body border-strokes h-0 border-t border-solid"
          >
            <td class="text-left">
              {{ variant.name }}
            </td>
            <td>
              <RfInput
                :modelValue="trafficPercentageValues[variant.id]"
                variant="outlined"
                type="number"
                size="small"
                step="1"
                min="0"
                :max="trafficPercentageValues[variant.id] + maxAvailableTraffic"
                class="flex! max-w-77.5 rounded"
                @update:modelValue="v => updateTrafficValue(v, variant.id)"
              >
                <template #append>
                  <button
                    v-if="trafficPercentageValues[variant.id] || maxAvailableTraffic"
                    class="inline-flex place-content-center px-1"
                    :title="
                      trafficPercentageValues[variant.id]
                        ? 'Clear traffic'
                        : 'Fill with remaining traffic'
                    "
                    @click="
                      updateTrafficValue(
                        trafficPercentageValues[variant.id] ? 0 : maxAvailableTraffic,
                        variant.id,
                      )
                    "
                  >
                    <RfCloseIcon
                      v-if="trafficPercentageValues[variant.id]"
                      class="fill-beige-50! h-6! w-6!"
                    />
                    <RfArrowUpIcon v-else class="fill-beige-50! h-6! w-6!" />
                  </button>
                </template>
              </RfInput>
            </td>
          </tr>
        </template>
      </RfTable>
    </template>
    <template #actions>
      <RfButtonNew
        size="large"
        type="tertiary"
        @click="(trafficEditDialog?.close(), emit('close'))"
      >
        Close
      </RfButtonNew>
      <RfButtonNew
        size="large"
        :disabled="totalSumTraffic !== 100 && 'Traffic distribution must add up to 100%'"
        :type="showRunButton ? 'secondary' : 'main'"
        @click="saveTraffic()"
      >
        Save {{ showRunButton ? "& Exit" : "" }}
      </RfButtonNew>

      <RfButtonNew
        v-if="showRunButton"
        size="large"
        :disabled="totalSumTraffic !== 100 && 'Traffic distribution must add up to 100%'"
        @click="saveTraffic(true)"
      >
        Run
      </RfButtonNew>
    </template>
  </RfBaseModal>
</template>

<script setup>
import RfTable from "@/blocks/RfTable/RfTable.vue";
import RfSortButton from "@/components/buttons/RfSortButton.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfBaseModal from "@/components/modals/RfBaseModal.vue";
import RfAlert from "@/components/alerts/RfAlert.vue";
import { computed, ref, useTemplateRef } from "vue";
import RfInput from "@/components/inputs/RfInput.vue";
import RfCloseIcon from "@/components/icons/RfCloseIcon.vue";
import RfArrowUpIcon from "@/components/icons/RfArrowUpIcon.vue";

const props = defineProps({
  variations: { type: Array, default: () => [] },
  showPromptMessage: { type: Boolean, default: false },
  isLiveEditable: { type: Boolean, default: false },
});
const trafficEditDialog = useTemplateRef("trafficEditDialog");
const emit = defineEmits(["primary", "close"]);
const trafficPercentageValues = ref({});
const showRunButton = ref(false);

const copyTraffic = () => {
  trafficPercentageValues.value = Object.fromEntries(
    props.variations.map(v => [v.id, Number(v.traffic_percent) || 0]),
  );
};

const maxAvailableTraffic = computed(() => {
  const trafficRest = 100 - totalSumTraffic.value;
  return trafficRest >= 0 ? trafficRest : 0;
});

const totalSumTraffic = computed(() =>
  Object.values(trafficPercentageValues.value).reduce((a, b) => a + Number(b), 0),
);

const saveTraffic = runPrompt => {
  emit("primary", {
    variations: props.variations.map(v => ({
      ...v,
      traffic_percent: trafficPercentageValues.value[v.id],
    })),
    runPrompt,
  });
  trafficEditDialog.value?.close();
};

const updateTrafficValue = (value, variantId) => {
  const n = Math.floor(Number(value));
  trafficPercentageValues.value = {
    ...trafficPercentageValues.value,
    [variantId]: Number.isFinite(n) ? Math.max(0, n) : 0,
  };
};

defineExpose({
  show: ({ showRun = false } = { showRun: false }) => {
    showRunButton.value = showRun;
    copyTraffic();
    trafficEditDialog.value?.show();
  },
  close: () => trafficEditDialog.value?.close(),
});
</script>
